import { BaseTextFieldProps, InputAdornment, TextField, useForkRef } from '@mui/material';
import { Control, useController } from 'react-hook-form';
import { useFieldStateError } from './useFieldStateError';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { formatPercentValue, parseIncompleteFloatValue, parsePercentValue } from '@razz-team/utils';

export interface PercentFieldProps extends BaseTextFieldProps {
  name: string;
  control?: Control;
  min?: number;
  max?: number;
  required?: boolean;
}

export function PercentField({
  name,
  control,
  min,
  max,
  required,
  inputRef,
  error,
  helperText,
  ...props
}: PercentFieldProps) {
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      min,
      max,
      required,
    },
  });

  const [value, setValue] = useState(() =>
    typeof field.value === 'number' ? formatPercentValue(field.value) : '',
  );

  useEffect(() => {
    if (!field.value) {
      setValue('');
    }
  }, [field.value]);

  const fieldError = useFieldStateError(props.label, fieldState.error);
  const forkedInputRef = useForkRef(field.ref, inputRef);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue(parseIncompleteFloatValue(event.target.value));
      field.onChange(parsePercentValue(event.target.value));
    },
    [field],
  );

  return (
    <TextField
      {...props}
      inputRef={forkedInputRef}
      name={field.name}
      value={value}
      disabled={field.disabled}
      onBlur={field.onBlur}
      onChange={handleChange}
      required={Boolean(required)}
      error={error || fieldState.invalid}
      helperText={error ? helperText : (fieldError ?? helperText)}
      slotProps={{
        input: {
          disabled: field.disabled,
          startAdornment: <InputAdornment position="start">%</InputAdornment>,
          inputProps: {
            min: 0,
            max: 100,
          },
        },
      }}
    />
  );
}
